import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	ArrowDownLeft,
	ArrowUpRight,
	Check,
	ChevronRight,
	Clock3,
	LoaderCircle,
	Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { api, type Transaction } from "@/client";
import { TransactionFiscalDocuments } from "@/components/TransactionFiscalDocuments";
import { Checks } from "@/domain/checks";
import { classificationFormSchema, type ClassificationForm } from "@/domain/classification";
import { INBOX_SOURCE_DESCRIPTIONS } from "@/domain/inbox";
import { EXPENSE_CATEGORIES, OPERATING_EXPENSE_DESTINATION } from "@/domain/operating-expenses";
import type { ReceiptMovementClassification } from "@/domain/receipts";
import { FinancePresentation } from "@/presentation/finance";

const destinationSuggestions = [
	"Açúcar",
	"Embalagens",
	"Frete de entrada",
	"Frete de saída — CIF",
	"GNRE",
	"Despesa operacional",
	"Conta corrente de sócio",
	"Caixa físico",
	"Transferência entre contas próprias",
	"Tributo",
	"Financiamento",
	"Outro destino",
];

export function TransactionWorkspace({
	transactions,
	filters,
	inferredClassifications,
}: {
	transactions: Transaction[];
	filters: React.ReactNode;
	inferredClassifications?: ReadonlyMap<string, ReceiptMovementClassification>;
}) {
	const queryClient = useQueryClient();
	const [selectedId, setSelectedId] = useState<string>();
	const [savedMessage, setSavedMessage] = useState<string>();
	const classifyMutation = useMutation({
		mutationFn: (input: ClassificationForm & { transactionId: string }) =>
			api.transactions.classify(input),
		onSuccess: async () => {
			setSavedMessage("Classificação confirmada");
			await queryClient.invalidateQueries({ queryKey: ["transactions", "current-month"] });
		},
	});

	useEffect(() => {
		if (!selectedId || !transactions.some((transaction) => transaction.id === selectedId)) {
			setSelectedId(transactions.at(0)?.id);
		}
	}, [selectedId, transactions]);

	const selected = transactions.find((transaction) => transaction.id === selectedId);

	return (
		<section className="overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-[0_20px_60px_-35px_rgba(28,25,23,0.35)]">
			<div className="grid min-h-[680px] xl:grid-cols-[minmax(560px,1.2fr)_minmax(360px,0.8fr)]">
				<div className="border-b border-stone-200 xl:border-r xl:border-b-0">
					{filters}

					<div className="max-h-[610px] overflow-y-auto">
						{transactions.length === 0 ? (
							<div className="grid min-h-72 place-items-center px-8 text-center text-sm text-stone-500">
								Nenhuma transação corresponde aos filtros atuais.
							</div>
						) : (
							transactions.map((transaction) => (
								<TransactionRow
									key={transaction.id}
									transaction={transaction}
									inferredClassification={inferredClassifications?.get(transaction.id)}
									selected={transaction.id === selectedId}
									onSelect={() => {
										setSelectedId(transaction.id);
										setSavedMessage(undefined);
									}}
								/>
							))
						)}
					</div>
				</div>

				<ClassificationPanel
					transaction={selected}
					inferredClassification={selected && inferredClassifications?.get(selected.id)}
					isSaving={classifyMutation.isPending}
					error={classifyMutation.error}
					savedMessage={savedMessage}
					onSave={(form) => {
						if (!selected) {
							return;
						}

						classifyMutation.mutate({ transactionId: selected.id, ...form });
					}}
				/>
			</div>
		</section>
	);
}

function TransactionRow({
	transaction,
	inferredClassification,
	selected,
	onSelect,
}: {
	transaction: Transaction;
	inferredClassification?: ReceiptMovementClassification;
	selected: boolean;
	onSelect: () => void;
}) {
	const title = transaction.supplier?.name || transaction.receiverName || transaction.description;
	const effectiveClassification =
		transaction.classification ??
		(transaction.payableObligation
			? {
					destination: transaction.payableObligation.destination,
					expenseCategory: transaction.payableObligation.expenseCategory,
				}
			: inferredClassification);
	const financiallyReconciled =
		!!transaction.payableObligation || Checks.isOutboundFreightReconciled(transaction);

	return (
		<button
			type="button"
			onClick={onSelect}
			className={`grid w-full grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-3 border-b border-stone-100 px-4 py-3 text-left transition last:border-b-0 sm:px-5 ${selected ? "bg-emerald-50/70" : "hover:bg-stone-50"}`}
		>
			<div
				className={`grid size-8 place-items-center rounded-lg ${transaction.direction === "DEBIT" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}
			>
				{transaction.direction === "DEBIT" ? (
					<ArrowUpRight className="size-4" />
				) : (
					<ArrowDownLeft className="size-4" />
				)}
			</div>
			<div className="min-w-0">
				<div className="flex min-w-0 items-center gap-2">
					<p className="truncate text-sm font-semibold text-stone-900">{title}</p>
					{transaction.supplier && (
						<span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
							{FinancePresentation.supplierKind(transaction.supplier.kind)}
						</span>
					)}
					{transaction.bankStatus === "PENDING" && (
						<span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
							Banco pendente
						</span>
					)}
				</div>
				<p className="mt-1 flex items-center gap-2 truncate text-xs text-stone-500">
					<span>{FinancePresentation.transactionDate(transaction.occurredOn)}</span>
					<span aria-hidden="true">·</span>
					<span className={effectiveClassification ? "text-emerald-700" : "text-amber-700"}>
						{effectiveClassification
							? classificationLabel(effectiveClassification)
							: "Pendente de classificação"}
					</span>
					{financiallyReconciled && (
						<>
							<span aria-hidden="true">·</span>
							<span className="font-medium text-emerald-800">Conciliado</span>
						</>
					)}
					{transaction.treasuryTransfer && (
						<>
							<span aria-hidden="true">·</span>
							<span className="text-emerald-800">{treasuryRoute(transaction)}</span>
						</>
					)}
				</p>
			</div>
			<div className="flex items-center gap-2 pl-2">
				<div className="text-right">
					<p
						className={`text-sm font-semibold tabular-nums ${transaction.direction === "DEBIT" ? "text-stone-950" : "text-emerald-800"}`}
					>
						{transaction.direction === "DEBIT" ? "−" : "+"}
						{FinancePresentation.money(Math.abs(transaction.amountCents))}
					</p>
					{effectiveClassification && <Check className="mt-1 ml-auto size-3.5 text-emerald-700" />}
				</div>
				<ChevronRight className={`size-4 ${selected ? "text-emerald-800" : "text-stone-300"}`} />
			</div>
		</button>
	);
}

function ClassificationPanel({
	transaction,
	inferredClassification,
	isSaving,
	error,
	savedMessage,
	onSave,
}: {
	transaction?: Transaction;
	inferredClassification?: ReceiptMovementClassification;
	isSaving: boolean;
	error: Error | null;
	savedMessage?: string;
	onSave: (form: ClassificationForm) => void;
}) {
	const effectiveClassification =
		transaction?.classification ??
		(transaction?.payableObligation
			? {
					destination: transaction.payableObligation.destination,
					expenseCategory: transaction.payableObligation.expenseCategory,
				}
			: inferredClassification);
	const financiallyReconciled = transaction
		? !!transaction.payableObligation || Checks.isOutboundFreightReconciled(transaction)
		: false;
	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<ClassificationForm>({
		resolver: arktypeResolver(classificationFormSchema),
		defaultValues: { explanation: "", destination: "", expenseCategoryId: null },
	});
	const destination = watch("destination");

	useEffect(() => {
		reset({
			explanation: classificationExplanation(transaction, inferredClassification),
			destination: effectiveClassification?.destination ?? "",
			expenseCategoryId:
				transaction?.classification?.expenseCategory?.id ??
				transaction?.payableObligation?.expenseCategory?.id ??
				null,
		});
	}, [effectiveClassification, inferredClassification, reset, transaction]);

	if (!transaction) {
		return (
			<div className="grid min-h-80 place-items-center p-8 text-center text-sm text-stone-500">
				Selecione uma transação para compreendê-la.
			</div>
		);
	}
	const payableDocuments =
		transaction.payableObligation?.documents.filter(
			(document) => document.associationState === "LINKED",
		) ?? [];
	const suggestedPayableDocuments =
		transaction.payableObligation?.documents.filter(
			(document) => document.associationState === "SUGGESTED",
		) ?? [];
	const payableDocumentIds = new Set(
		transaction.payableObligation?.documents.map((document) => document.id),
	);
	const directDocuments = transaction.fiscalDocuments.filter(
		(document) => !payableDocumentIds.has(document.id),
	);
	const hasDocuments =
		payableDocuments.length > 0 ||
		suggestedPayableDocuments.length > 0 ||
		directDocuments.length > 0;

	return (
		<aside className="flex min-h-0 flex-col overflow-y-auto bg-[#fbfbf7]">
			<div className="border-b border-stone-200 p-5 sm:p-6">
				<div className="flex items-start justify-between gap-4">
					<div>
						<p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
							Compreender transação
						</p>
						<h2 className="mt-2 text-xl font-semibold tracking-[-0.025em] text-stone-950">
							{transaction.supplier?.name || transaction.receiverName || transaction.description}
						</h2>
					</div>
					<p
						className={`shrink-0 text-lg font-semibold tabular-nums ${transaction.direction === "CREDIT" ? "text-emerald-800" : "text-stone-950"}`}
					>
						{transaction.direction === "DEBIT" ? "−" : "+"}
						{FinancePresentation.money(Math.abs(transaction.amountCents))}
					</p>
				</div>
				<p className="mt-2 text-xs leading-5 text-stone-500">
					{transaction.descriptionRaw || transaction.description}
				</p>
				{transaction.treasuryTransfer && (
					<p className="mt-3 inline-flex rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-900">
						{treasuryRoute(transaction)}
					</p>
				)}
			</div>

			<div className="border-b border-stone-200 p-5 sm:p-6">
				<div className="mb-3 flex items-center gap-2 text-xs font-semibold text-stone-700">
					<Sparkles className="size-4 text-amber-600" />
					Sinais disponíveis para sua decisão
				</div>
				<div className="flex flex-wrap gap-2">
					{transaction.supplier && (
						<>
							<SourceSignal label="Fornecedor" value={transaction.supplier.name} />
							<SourceSignal
								label="Tipo"
								value={FinancePresentation.supplierKind(transaction.supplier.kind)}
							/>
							<SourceSignal label="Fornece" value={transaction.supplier.category.name} />
							{transaction.supplier.taxId && (
								<SourceSignal
									label="CNPJ"
									value={FinancePresentation.taxId(transaction.supplier.taxId)}
								/>
							)}
						</>
					)}
					{transaction.category && (
						<SourceSignal label="Categoria Pluggy" value={transaction.category} />
					)}
					{transaction.paymentMethod && (
						<SourceSignal label="Meio" value={transaction.paymentMethod} />
					)}
					{transaction.receiverName && (
						<SourceSignal label="Recebedor" value={transaction.receiverName} />
					)}
					{transaction.treasuryTransfer && (
						<>
							<SourceSignal
								label="Conta de origem"
								value={transaction.treasuryTransfer.fromAccount.name}
							/>
							<SourceSignal
								label="Conta de destino"
								value={transaction.treasuryTransfer.toAccount.name}
							/>
						</>
					)}
					<SourceSignal
						label="Estado bancário"
						value={transaction.bankStatus === "POSTED" ? "Efetivada" : "Pendente"}
					/>
					{financiallyReconciled && (
						<SourceSignal
							label="Conciliação financeira"
							value={transaction.payableObligation ? "Obrigação paga" : "Concluída"}
						/>
					)}
					{transaction.classification?.expenseCategory && (
						<SourceSignal
							label="Categoria de despesa"
							value={transaction.classification.expenseCategory.name}
						/>
					)}
				</div>
				<p className="mt-3 text-xs leading-5 text-stone-500">
					{sourceExplanation(transaction, inferredClassification)}
				</p>
			</div>

			{transaction.payableObligation && (
				<div className="border-b border-stone-200 p-5 sm:p-6">
					<p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
						Obrigação que originou o pagamento
					</p>
					<div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
						<EvidenceFact label="Credor" value={transaction.payableObligation.creditorName} />
						<EvidenceFact
							label="Vencimento"
							value={FinancePresentation.transactionDate(transaction.payableObligation.dueOn)}
						/>
						<EvidenceFact
							label="Classificação"
							value={`${transaction.payableObligation.destination}${transaction.payableObligation.expenseCategory ? ` → ${transaction.payableObligation.expenseCategory.name}` : ""}`}
						/>
						<EvidenceFact
							label="Origem"
							value={`Caixa de entrada · ${INBOX_SOURCE_DESCRIPTIONS[transaction.payableObligation.source]}`}
						/>
					</div>
					<p className="mt-3 text-xs leading-5 text-stone-500">
						{transaction.payableObligation.description}
					</p>
					<p className="mt-2 text-[11px] leading-4 text-stone-400">
						Reconhecida por {transaction.payableObligation.recognizedBy} em{" "}
						{FinancePresentation.moment(transaction.payableObligation.recognizedAt)} e conciliada
						por fornecedor, valor e data exatos.
					</p>
				</div>
			)}

			{hasDocuments && (
				<div className="border-b border-stone-200 p-5 sm:p-6">
					<p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
						Documentos
					</p>
					<div className="space-y-4">
						<TransactionFiscalDocuments
							documents={payableDocuments}
							linkExplanation="Documento preservado desde a Caixa de entrada e agora disponível no pagamento conciliado. A Qive continua sendo a fonte do documento."
						/>
						<TransactionFiscalDocuments documents={directDocuments} />
						{suggestedPayableDocuments.length > 0 && (
							<div>
								<p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
									Para conferir
								</p>
								<TransactionFiscalDocuments
									documents={suggestedPayableDocuments}
									linkExplanation="Possível documento preservado desde a Caixa de entrada; permanece como sugestão até a confirmação do vínculo."
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<form onSubmit={handleSubmit(onSave)} className="flex flex-1 flex-col p-5 sm:p-6">
				<label className="text-sm font-semibold text-stone-900" htmlFor="explanation">
					O que é esta transação?
				</label>
				<textarea
					id="explanation"
					rows={4}
					placeholder="Ex.: pagamento do frete da entrega…"
					className="mt-2 w-full resize-none rounded-xl border border-stone-200 bg-white px-3.5 py-3 text-sm leading-6 outline-none transition placeholder:text-stone-400 focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
					{...register("explanation")}
				/>
				{errors.explanation && (
					<p className="mt-1.5 text-xs text-red-700">Informe uma explicação.</p>
				)}

				<label className="mt-5 text-sm font-semibold text-stone-900" htmlFor="destination">
					Qual é o destino?
				</label>
				<input
					id="destination"
					list="destination-suggestions"
					placeholder="Escolha ou escreva um destino"
					className="mt-2 h-11 w-full rounded-xl border border-stone-200 bg-white px-3.5 text-sm outline-none transition placeholder:text-stone-400 focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
					{...register("destination")}
				/>
				<datalist id="destination-suggestions">
					{destinationSuggestions.map((suggestion) => (
						<option key={suggestion} value={suggestion} />
					))}
				</datalist>
				{errors.destination && <p className="mt-1.5 text-xs text-red-700">Informe um destino.</p>}

				{destination.trim() === OPERATING_EXPENSE_DESTINATION && (
					<>
						<label
							className="mt-5 text-sm font-semibold text-stone-900"
							htmlFor="expenseCategoryId"
						>
							Qual é a categoria da despesa?
						</label>
						<select
							id="expenseCategoryId"
							className="mt-2 h-11 w-full rounded-xl border border-stone-200 bg-white px-3.5 text-sm outline-none transition focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
							{...register("expenseCategoryId", {
								setValueAs: (value) => value || null,
							})}
						>
							<option value="">Selecione uma categoria</option>
							{EXPENSE_CATEGORIES.map((category) => (
								<option key={category.id} value={category.id}>
									{category.name}
								</option>
							))}
						</select>
						{errors.expenseCategoryId && (
							<p className="mt-1.5 text-xs text-red-700">Selecione uma categoria.</p>
						)}
					</>
				)}

				<div className="mt-auto pt-6">
					{transaction.classification && (
						<p className="mb-3 flex items-center gap-2 text-xs text-stone-500">
							<Clock3 className="size-3.5" />
							{classificationHistory(transaction.classification)}
							{FinancePresentation.moment(transaction.classification.classifiedAt)}
						</p>
					)}
					{!transaction.classification && transaction.payableObligation && (
						<p className="mb-3 flex items-center gap-2 text-xs text-stone-500">
							<Clock3 className="size-3.5" />
							Classificação preservada desde a obrigação reconhecida
						</p>
					)}
					{!transaction.classification &&
						!transaction.payableObligation &&
						inferredClassification && (
							<p className="mb-3 flex items-center gap-2 text-xs text-stone-500">
								<Clock3 className="size-3.5" />
								{receiptClassificationHistory(inferredClassification)}
							</p>
						)}
					{savedMessage && (
						<p className="mb-3 flex items-center gap-2 text-xs font-medium text-emerald-800">
							<Check className="size-4" />
							{savedMessage}
						</p>
					)}
					{error && <p className="mb-3 text-xs text-red-700">{error.message}</p>}
					<button
						type="submit"
						disabled={isSaving}
						className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-900 disabled:cursor-wait disabled:opacity-60"
					>
						{isSaving ? (
							<LoaderCircle className="size-4 animate-spin" />
						) : (
							<Check className="size-4" />
						)}
						{effectiveClassification ? "Confirmar correção" : "Confirmar classificação"}
					</button>
					<p className="mt-3 text-center text-[11px] leading-4 text-stone-400">
						A correção cria um novo evento e preserva o histórico anterior.
					</p>
				</div>
			</form>
		</aside>
	);
}

function sourceExplanation(
	transaction: Transaction,
	inferredClassification?: ReceiptMovementClassification,
) {
	if (transaction.classification) {
		switch (transaction.classification.source) {
			case "CHECK_DESTINATION_RULE":
				if (Checks.isOutboundFreightReconciled(transaction)) {
					return "O movimento bancário efetivado, o número do cheque e o destino confirmado conciliam financeiramente este pagamento de frete de saída. A alocação a CT-es e entregas permanece separada.";
				}

				return "O destino confirmado do cheque sustenta esta classificação; uma correção preserva o histórico e recalcula a tesouraria quando necessário.";
			case "DAE_RULE":
				return "O documento pago no DAE SINDI e a correspondência bancária única sustentam a classificação automática como GNRE; uma pessoa pode corrigir a decisão.";
			case "BANK_FEE_RULE":
				return "A descrição do movimento bancário efetivado corresponde à regra autorizada de tarifa. A categoria da Pluggy não decide esta classificação; uma pessoa pode corrigi-la.";
			case "COLLABORATOR_RULE":
				return "A descrição SISPAG SALÁRIOS identifica de forma determinística um pagamento de folha ou férias. Outros gastos relacionados a colaboradores exigem confirmação própria.";
			case "ENERGY_RULE":
				return "A identidade bancária exata da CEMIG sustenta a classificação automática como energia elétrica. A competência e a conciliação com cada fatura permanecem separadas.";
			case "SUPPLIER_RULE":
				return "O fornecedor reconhecido e seu fornecimento cadastrado sustentam a regra automática; uma pessoa pode corrigir a decisão.";
			case "HUMAN":
				return "Uma pessoa confirmou esta explicação e pode corrigi-la sem apagar o histórico anterior.";
			default:
				throw new Error("Fonte de classificação inválida");
		}
	}

	if (transaction.payableObligation) {
		return "A obrigação reconhecida foi ligada de forma única a esta saída bancária. Sua classificação e seus documentos seguem disponíveis aqui sem criar uma nova fonte de verdade.";
	}

	if (inferredClassification?.basis === "GUARDIAN_RECONCILIATION") {
		return "A entrada bancária e as baixas balanceadas do contas a receber do Guardian sustentam esta classificação; uma pessoa pode corrigir a decisão.";
	}

	if (inferredClassification?.basis === "BANK_DESCRIPTION_RULE") {
		if (transaction.treasuryTransfer) {
			return `O livro de tesouraria identificou esta entrada como uma transferência de ${transaction.treasuryTransfer.fromAccount.name} para ${transaction.treasuryTransfer.toAccount.name}; uma pessoa pode corrigir a decisão.`;
		}

		return "Uma regra determinística autorizada identificou a natureza bancária desta entrada; uma pessoa pode corrigir a decisão.";
	}

	if (transaction.supplier) {
		return "O fornecedor reconhecido e seu fornecimento cadastrado sustentam a regra automática; uma pessoa pode corrigir a decisão.";
	}

	return "Esses sinais ajudam, mas não classificam a transação sozinhos.";
}

function classificationExplanation(
	transaction: Transaction | undefined,
	inferredClassification: ReceiptMovementClassification | undefined,
) {
	if (!transaction) {
		return "";
	}

	if (transaction.classification) {
		return transaction.classification.explanation;
	}

	if (transaction.payableObligation) {
		return transaction.payableObligation.description;
	}

	if (inferredClassification?.basis === "BANK_DESCRIPTION_RULE" && transaction.treasuryTransfer) {
		return `Transferência entre contas próprias: ${treasuryRoute(transaction)}`;
	}

	return inferredClassification?.explanation ?? "";
}

function treasuryRoute(transaction: Transaction) {
	if (!transaction.treasuryTransfer) {
		throw new Error("Transação sem transferência de tesouraria");
	}

	return `${transaction.treasuryTransfer.fromAccount.name} → ${transaction.treasuryTransfer.toAccount.name}`;
}

function receiptClassificationHistory(classification: ReceiptMovementClassification) {
	if (classification.basis === "GUARDIAN_RECONCILIATION") {
		return "Classificação sustentada pela conciliação com o A Receber do Guardian";
	}

	return "Classificação automática pela natureza bancária identificada";
}

function classificationHistory(classification: NonNullable<Transaction["classification"]>) {
	switch (classification.source) {
		case "CHECK_DESTINATION_RULE":
			return `Destino do cheque confirmado por ${classification.actor}, `;
		case "DAE_RULE":
			return "Classificação automática pelo DAE SINDI, ";
		case "BANK_FEE_RULE":
			return "Classificação automática pela regra de tarifa bancária, ";
		case "COLLABORATOR_RULE":
			return "Classificação automática pela regra de SISPAG Salários, ";
		case "ENERGY_RULE":
			return "Classificação automática pela identidade bancária da CEMIG, ";
		case "SUPPLIER_RULE":
			return "Classificação automática pela regra do fornecedor, ";
		case "HUMAN":
			return `Última confirmação por ${classification.actor}, `;
		default:
			throw new Error("Fonte de classificação inválida");
	}
}

function classificationLabel(classification: {
	destination: string;
	expenseCategory?: { name: string } | null;
}) {
	if (!classification.expenseCategory) {
		return classification.destination;
	}

	return `${classification.destination} · ${classification.expenseCategory.name}`;
}

function SourceSignal({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-stone-200 bg-white px-3 py-2">
			<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</p>
			<p className="mt-0.5 max-w-48 truncate text-xs font-semibold text-stone-700">{value}</p>
		</div>
	);
}

function EvidenceFact({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</p>
			<p className="mt-0.5 font-semibold text-stone-700">{value}</p>
		</div>
	);
}
